import { Box } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import DropDown from "../common/Dropdown/Dropdown";
import { useDispatch, useSelector } from "react-redux";
import {
  updateDates,
  updateTimeframe,
} from "../../redux/features/transactionsSlice";
import { timeframeOptions, YYYYMMDD } from "../../variables/constants";
import { LoadingButton } from "@mui/lab";
import "./styles.scss";
import CustomDateRange from "../common/Dialog/CustomDateRange";
import { RootState } from "../../redux/store";
import { apiEndPoints } from "../../variables/apiEndpoints";
import useFileDownloader from "../../hooks/useFileDownloader";
import CustomSnackbar from "../common/Snackbar/CustomSnackbar";
import { convertToLocal } from "../../utils/dateFormat";

type Format = "pdf" | "csv";

interface Props {
  showClearFilters?: Boolean;
  clearFilters?: Function;
  /** Transaction IDs that pass the current DataGrid filters (used for export). */
  transactionIds?: number[];
}

const TableFilters = ({
  showClearFilters = false,
  clearFilters = () => {},
  transactionIds,
}: Props) => {
  const { timeframe, startFilterDate, endFilterDate } = useSelector(
    (state: RootState) => state.transactionDetailsState
  );
  const { downloadFile } = useFileDownloader();
  const [timeFrame, setTimeFrame] = useState(timeframe ?? "");
  const [downloadingDocument, setDownloadingDocument] = useState<
    Format | undefined
  >();
  const [onSuccess, setOnSuccess] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    dispatch(updateTimeframe(timeFrame));
    timeFrame === "2" && setOpen(true);
  }, [timeFrame]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleApplyClose = () => {
    setOpen(false);
    setTimeFrame("0");
    dispatch(updateTimeframe("0"));
  };

  const handleDownload = useCallback(
    async (format: Format) => {
      setDownloadingDocument(format);
      const startDate = convertToLocal(startFilterDate).format(YYYYMMDD);
      const endDate = convertToLocal(endFilterDate).format(YYYYMMDD);
      const body: any = {
        startDate,
        endDate,
        format,
      };
      if (transactionIds?.length) {
        body.transactionIds = transactionIds;
      }
      const result = await downloadFile({
        url: `/reports${apiEndPoints.transactions}`,
        method: "post",
        fileName: `Transactions.${format}`,
        data: body,
      });
      setDownloadingDocument(undefined);
      if (!result.success) {
        setSnackbarMsg(result.errorMessage || "Error downloading file");
        setOnSuccess(true);
      }
    },
    [downloadFile, startFilterDate, endFilterDate, transactionIds]
  );

  return (
    <Box
      sx={{
        display: "flex",
        padding: "26px 0px 0px 26px",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <DropDown
        val={timeFrame}
        setVal={setTimeFrame}
        options={timeframeOptions}
        label="Time Frame"
        width="236px"
        height="36px"
        handleCustomClick={(value: string) => {
          if (value === "2") {
            setOpen(true);
          }
        }}
      />
      <CustomDateRange
        handleClose={handleClose}
        open={open}
        handleApplyClose={handleApplyClose}
        updateDates={updateDates}
      />
      <Box>
        {showClearFilters ? (
          <LoadingButton
            variant="outlined"
            data-testid="exportPdfBtn"
            size="small"
            className="export-button"
            onClick={() => clearFilters()}
            sx={{ marginRight: "26px", width: "200px", height: "36px" }}
          >
            {"CLEAR FILTERS"}
          </LoadingButton>
        ) : null}
        <LoadingButton
          variant="outlined"
          data-testid="exportPdfBtn"
          size="small"
          className="export-button"
          onClick={() => handleDownload("pdf")}
          loading={downloadingDocument === "pdf"}
          sx={{ marginRight: "26px", width: "117px", height: "36px" }}
        >
          {"EXPORT PDF"}
        </LoadingButton>
        <LoadingButton
          variant="outlined"
          data-testid="exportCsvBtn"
          size="small"
          className="export-button"
          onClick={() => handleDownload("csv")}
          loading={downloadingDocument === "csv"}
          sx={{ marginRight: "26px", width: "137px", height: "36px" }}
        >
          {"EXPORT EXCEL"}
        </LoadingButton>
      </Box>
      <CustomSnackbar
        open={onSuccess}
        message={snackbarMsg}
        setOpen={setOnSuccess}
      />
    </Box>
  );
};

export default TableFilters;
